import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { RefreshCw, Calendar, ShieldAlert, TrendingUp, Trophy } from "lucide-react";
import GaugeChart from "./GaugeChart";

interface QuestionMeta {
  category: string;
  question: string;
  options: { value: number; label: string }[];
}

interface DiagnosticResultProps {
  score: number;
  onRestart: () => void;
  answers?: Record<number, number>;
  questions?: QuestionMeta[];
}

const getCategoryFeedback = (category: string, value: number): string => {
  const map: Record<string, { 0: string; 5: string; 10: string }> = {
    "Lealtad Patrimonial": {
      0: "Tu base de clientes es frágil: compite por precio, no por valor de marca. Necesitás construir vínculos basados en autoridad.",
      5: "Hay lealtad parcial, pero todavía dependés del precio para retener. Tu marca aún no es razón suficiente para quedarse.",
      10: "Excelente: tu marca genera lealtad real. Los clientes te eligen por trayectoria, no por tarifa.",
    },
    "Identidad Propietaria": {
      0: "Tu identidad visual y comunicacional no tiene sello propio. El mercado te ve como uno más del montón.",
      5: "Tenés algunos elementos reconocibles, pero falta consistencia para que tu marca sea inconfundible.",
      10: "Tu identidad es propietaria y reconocible. Eso es un activo de altísimo valor.",
    },
    "Velocidad de Capitalización": {
      0: "Tu propuesta de valor no se entiende rápido. Eso te cuesta oportunidades comerciales y ralentiza el cierre.",
      5: "Tu valor se capta con algo de contexto, pero falta claridad y síntesis estratégica.",
      10: "Tu marca comunica valor en segundos. Eso acelera ventas, alianzas y captación de capital.",
    },
    "Premium Price": {
      0: "Estás compitiendo por precio. Tu marca no te permite cobrar un diferencial sostenible.",
      5: "A veces lográs cobrar premium, pero no de forma consistente. Hay margen oculto que no estás capturando.",
      10: "Tu marca te permite cobrar precios premium de forma sostenida. Eso es Brand Equity puro.",
    },
    "Seguridad de Activo": {
      0: "Tu marca no está protegida legalmente. Estás construyendo valor sobre un activo vulnerable.",
      5: "Tenés protección parcial. Hay flancos legales que pueden costarte caro si no los cerrás.",
      10: "Tu marca está blindada legalmente. Estás construyendo sobre terreno seguro.",
    },
    "Brand Extension": {
      0: "Cada nueva línea arranca desde cero. No estás capitalizando el valor acumulado de tu marca.",
      5: "Tu marca ayuda en extensiones, pero no es palanca suficiente para asegurar rentabilidad inmediata.",
      10: "Tu marca habilita expansión rentable y acelerada. Es un multiplicador real de oportunidades.",
    },
    "Riesgo de Devaluación": {
      0: "Dependés de promociones para sostener ventas. Eso devalúa tu marca y comprime tus márgenes.",
      5: "Mezclás ventas orgánicas con descuentos. Tu marca todavía no genera tracción suficiente por sí sola.",
      10: "Tus ventas fluyen por prestigio. Tu marca trabaja para vos, no al revés.",
    },
    "Consistencia de Marca": {
      0: "Tu mensaje cambia según la urgencia. Eso confunde al mercado y debilita tu autoridad.",
      5: "Hay consistencia parcial, pero las variaciones frecuentes diluyen tu posicionamiento.",
      10: "Tu mensaje es coherente y evoluciona estratégicamente. Eso construye autoridad sostenida.",
    },
    "Brand Equity Interno": {
      0: "Tu equipo cumple, pero no encarna la marca. Eso se traslada a la experiencia del cliente.",
      5: "Hay compromiso variable. Tu cultura de marca aún no es palanca interna fuerte.",
      10: "Tu equipo vive la marca. Eso multiplica todo: producto, servicio y reputación.",
    },
    "Multiplicador de Valor": {
      0: "Si vendieras la empresa, sólo pagarían por tangibles. Toda tu trayectoria se está liquidando al costo.",
      5: "Tu marca aporta algo al valor de venta, pero todavía no es el activo principal.",
      10: "Tu marca multiplica significativamente el valor de la empresa. Sos un caso de Brand Equity real.",
    },
  };
  const fallback = {
    0: "Hay una oportunidad clave de mejora en este punto.",
    5: "Vas en buen camino, pero hay margen para fortalecer este eje.",
    10: "Excelente desempeño en este eje estratégico.",
  };
  const entry = map[category] ?? fallback;
  return entry[value as 0 | 5 | 10] ?? entry[0];
};

interface ResultProfile {
  title: string;
  subtitle: string;
  mainText: string;
  opportunity: string;
  recommendation: string;
  closingText: string;
  icon: typeof ShieldAlert;
  colorClass: string;
  bgClass: string;
}

const getResultProfile = (score: number): ResultProfile => {
  if (score <= 30) {
    return {
      title: "Tu Marca está en peligro",
      subtitle: "Perfil 0-30",
      mainText:
        "El negocio funciona, pero tu marca todavía no es el activo que la trayectoria de tu empresa se merece. Hoy la marca es invisible para el mercado y eso la hace peligrosamente sustituible: si un competidor baja el precio, el negocio sufre porque la marca no tiene el peso suficiente para dictar las reglas. Aunque den el mejor producto o servicio, si la marca no lo comunica y el mercado no lo percibe, para el resto no existe.",
      opportunity:
        "Hay años de experiencia que la empresa está liquidando al costo. Si dejamos de vender \"productos y servicios\" y empezamos a construir una marca de referencia, el negocio va a pasar de ser un simple proveedor a ser un activo que crece por su propio nombre.",
      recommendation:
        "Es clave empezar a blindar el patrimonio de la empresa y convertir la marca en un activo urgente para dejar de perder valor. Ya trabajaron mucho para otros; es hora de que la marca trabaje para el negocio.",
      closingText:
        "Este caso tiene mucho potencial pero la marca está frenada. Por haber completado el diagnóstico, te invitamos a una Llamada de Claridad 100% gratuita dónde te daremos recomendaciones para tu caso y puedas transformar la operación en una marca que valga de verdad.",
      icon: ShieldAlert,
      colorClass: "text-[#470628]",
      bgClass: "bg-[#FCC3D7] border-[#FF9FBD]",
    };
  }
  
  if (score <= 70) {
    return {
      title: "Tenés una marca desaprovechada",
      subtitle: "Perfil 40-70",
      mainText:
        "La empresa logró lo más difícil: seguir existiendo. Pero hay un problema: la marca es un \"activo silencioso\". Debería transmitir el prestigio que la empresa realmente tiene, pero hoy la comunicación no está a la altura. Están dejando que otros con menos talento se queden con los clientes y los márgenes que le pertenecen a esta marca.",
      opportunity:
        "La empresa está a un solo ajuste de dejar de \"pelear\" el mercado y empezar a dominarlo. El valor ya está construido; ahora solo falta que la marca aprenda a capturarlo en los precios y en su autoridad a través de la comunicación persuasiva y estratégica.",
      recommendation:
        "Es una instancia clave para cuidar los márgenes y capitalizar el prestigio de la marca antes de que la competencia gane terreno. No permitas que el esfuerzo de años se pierda por falta de una estrategia de marca clara.",
      closingText:
        "La marca está a un paso de la élite de la industria. Te invitamos a una Llamada de Claridad 100% gratuita dónde te daremos recomendaciones para tu caso y puedas descubrir dónde se está perdiendo valor y cómo proyectar el salto que esta empresa ya debería estar dando.",
      icon: TrendingUp,
      colorClass: "text-white",
      bgClass: "bg-[#8B0030] border-[#C4507A]",
    };
  }
  
  return {
    title: "Una marca de alto valor",
    subtitle: "Perfil 80-100",
    mainText:
      "¡Felicitaciones! Esta empresa logró lo que casi ninguna consigue: que su marca valga tanto o más que sus activos físicos. Es un activo financiero real con un nombre reputacional. Pero ojo: estar en la cima es un reto. Sin una estrategia sólida para proteger esa reputación, el legado de la marca corre peligro y se puede estancar.",
    opportunity:
      "La marca está en el momento ideal para multiplicarse (franquicias, expansión o un salto de escala). Hoy el nombre de la empresa es una moneda de cambio de altísimo valor; el secreto es trabajar su \"interés compuesto\" para que siga creciendo.",
    recommendation:
      "Ahora lo más importante es proteger y expandir el valor de la marca para que el activo sea cada vez más grande. Ya hicieron el esfuerzo de construir legado; ahora toca la recompensa de escalar. Las marcas de este nivel ya no se gestionan, se expanden.",
    closingText:
      "Este caso es excepcional. Por haber llegado hasta acá, te invitamos a una Llamada de Claridad 100% gratuita de alta dirección donde te daremos algunas recomendaciones para tu caso para proyectar el próximo gran salto de la marca y asegurar que sea verdaderamente imbatible.",
    icon: Trophy,
    colorClass: "text-[#FF9FBD]",
    bgClass: "bg-[#470628] border-[#600832]",
  };
};

const DiagnosticResult = ({ score, onRestart, answers, questions }: DiagnosticResultProps) => {
  const profile = getResultProfile(score);
  const Icon = profile.icon;

  const scrollToCalendly = () => {
    const calendlySection = document.getElementById('calendly');
    if (calendlySection) {
      calendlySection.scrollIntoView({ behavior: 'instant' });
    }
  };

  const isLowScore = score <= 30;

  return (
    <div className="text-center px-4 sm:px-0">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <span className="text-primary text-xs sm:text-sm font-semibold tracking-widest uppercase mb-4 block">
          Tu Resultado
        </span>
        <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold mb-6 sm:mb-8">
          Resultado de tu{" "}
          <span className="text-gradient-gold">Test de Capital de Marca</span>
        </h2>
      </motion.div>

      {/* Gauge Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="mb-8 sm:mb-12"
      >
        <GaugeChart score={score} />
      </motion.div>

      {/* Result Card */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.5 }}
        className={`relative overflow-hidden ${profile.bgClass} rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-10 mb-6 sm:mb-8 border-2`}
      >
        <div className="relative z-10">
          {/* Icon */}
          <div className="flex items-center justify-center mb-4 sm:mb-6">
            <motion.div 
              className={`p-3 sm:p-4 rounded-full ${isLowScore ? 'bg-[#470628]/10' : 'bg-white/10'}`}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 1.7, type: "spring", stiffness: 200 }}
            >
              <Icon className={`w-6 h-6 sm:w-8 sm:h-8 ${profile.colorClass}`} />
            </motion.div>
          </div>
          
          {/* Subtitle badge */}
          <motion.span 
            className={`inline-block text-[10px] sm:text-xs font-semibold tracking-[0.15em] sm:tracking-[0.2em] uppercase mb-2 sm:mb-3 px-3 sm:px-4 py-1 sm:py-1.5 rounded-full ${
              isLowScore 
                ? 'bg-[#470628]/10 text-[#470628]' 
                : 'bg-white/20 text-white'
            }`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.8 }}
          >
            {profile.subtitle}
          </motion.span>
          
          {/* Title */}
          <h3 className={`font-display text-xl sm:text-2xl md:text-3xl font-bold mb-4 sm:mb-6 ${
            isLowScore ? 'text-[#470628]' : 'text-white'
          }`}>
            {profile.title}
          </h3>
          
          {/* Elegant divider */}
          <div className={`w-12 sm:w-16 h-px mx-auto mb-4 sm:mb-6 ${
            isLowScore ? 'bg-[#470628]/30' : 'bg-white/30'
          }`} />
          
          {/* Main Text */}
          <div className="text-left max-w-2xl mx-auto mb-5 sm:mb-6">
            <p className={`text-sm sm:text-base leading-relaxed ${
              isLowScore ? 'text-[#470628]/90' : 'text-white/90'
            }`}>
              {profile.mainText}
            </p>
          </div>
          
          {/* Oportunidad */}
          <div className="text-left max-w-2xl mx-auto mb-5 sm:mb-6">
            <h4 className={`text-xs sm:text-sm font-semibold uppercase tracking-wider mb-2 ${
              isLowScore ? 'text-[#470628]' : 'text-white'
            }`}>
              Oportunidad
            </h4>
            <p className={`text-sm sm:text-base leading-relaxed ${
              isLowScore ? 'text-[#470628]/80' : 'text-white/80'
            }`}>
              {profile.opportunity}
            </p>
          </div>
          
          {/* Recomendación */}
          <div className="text-left max-w-2xl mx-auto mb-5 sm:mb-6">
            <h4 className={`text-xs sm:text-sm font-semibold uppercase tracking-wider mb-2 ${
              isLowScore ? 'text-[#470628]' : 'text-white'
            }`}>
              Recomendación
            </h4>
            <p className={`text-sm sm:text-base leading-relaxed ${
              isLowScore ? 'text-[#470628]/80' : 'text-white/80'
            }`}>
              {profile.recommendation}
            </p>
          </div>

          {/* Closing Text */}
          <div className={`text-center max-w-2xl mx-auto pt-4 sm:pt-6 border-t ${
            isLowScore ? 'border-[#470628]/20' : 'border-white/20'
          }`}>
            <p className={`text-sm sm:text-base leading-relaxed italic ${
              isLowScore ? 'text-[#470628]/90' : 'text-white/90'
            }`}>
              {profile.closingText}
            </p>
          </div>

          {/* CTA Button inside card */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 2 }}
            className="mt-6 sm:mt-8"
          >
            <Button
              variant="gold"
              size="lg"
              onClick={scrollToCalendly}
              className="w-full sm:w-auto text-sm sm:text-base px-6 sm:px-8 py-3 sm:py-4 font-bold"
            >
              <Calendar className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              APLICAR LLAMADA
            </Button>
          </motion.div>
        </div>
      </motion.div>

      {/* Per-question feedback */}
      {answers && questions && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 2.0 }}
          className="text-left mb-8 sm:mb-10"
        >
          <h3 className="font-display text-xl sm:text-2xl font-bold mb-4 text-center text-foreground">
            Análisis detallado por eje
          </h3>
          <p className="text-sm text-muted-foreground text-center mb-6 max-w-2xl mx-auto">
            Lectura específica de cada respuesta de tu diagnóstico.
          </p>
          <div className="space-y-4 max-w-2xl mx-auto">
            {questions.map((q, i) => {
              const value = answers[i] ?? 0;
              const selected = q.options.find(o => o.value === value);
              return (
                <div
                  key={i}
                  className="bg-card border border-border rounded-xl p-4 sm:p-5"
                >
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <span className="text-xs sm:text-sm font-semibold text-primary uppercase tracking-wider">
                      {i + 1}. {q.category}
                    </span>
                    <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                      value === 10 ? "bg-green-500/15 text-green-700 dark:text-green-400" :
                      value === 5 ? "bg-yellow-500/15 text-yellow-700 dark:text-yellow-400" :
                      "bg-red-500/15 text-red-700 dark:text-red-400"
                    }`}>
                      {value}/10
                    </span>
                  </div>
                  {selected && (
                    <p className="text-xs sm:text-sm text-muted-foreground italic mb-2">
                      Tu respuesta: "{selected.label}"
                    </p>
                  )}
                  <p className="text-sm sm:text-base text-foreground leading-relaxed">
                    {getCategoryFeedback(q.category, value)}
                  </p>
                </div>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Restart Button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 2.2 }}
      >
        <Button 
          variant="outline" 
          size="lg" 
          onClick={onRestart} 
          className="gap-2 text-sm sm:text-base"
        >
          <RefreshCw className="w-4 h-4" />
          Realizar otra vez
        </Button>
      </motion.div>
    </div>
  );
};

export default DiagnosticResult;
